import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useSystem } from '@ohif/core';
import { expandDropped, IntakeError } from '../utils/dicomIntake';
import { UploadRow, UploadStatus } from '../utils/uploadTypes';
import { runUploadBatch, rollbackStudies } from '../utils/uploadBatch';
import { describeError } from '../utils';

/**
 * D7. NO PubSub service, and the reducer is the sole owner of upload state.
 * PubSub earns its keep for state with multiple consumers or a lifetime outliving
 * the component; upload state has neither. D7 argued upstream ENFORCES the second,
 * and IT DOES NOT: the modal's close X is reachable throughout an upload (see the
 * unmount effect below), so this component can be torn down mid-batch. The
 * conclusion survives because the effect ends the batch with it — a lifetime that
 * cannot outlive the component is still not a reason for a service. develop
 * shipped both a per-file PubSubService and this reducer, and the service died
 * silently: updateStatus(InProgress|Success) is commented out at
 * DicomUploader.ts:144 and :160, and :160 is unreachable anyway, sitting after a
 * return (§6 defect 1).
 *
 * Consequence, recorded: with no service there is no SESSION_LOST emitter. That is
 * fine — viewerHttp already carries onUnauthorized -> handleUnauthenticated()
 * (http/index.ts:60), so a 401 mid-upload triggers the global redirect unaided.
 *
 * Plan D-3 imports nothing from fflate here on purpose: dicomIntake is imported by
 * DIRECT PATH, never re-exported through utils/index.ts, because that barrel is
 * imported by index.tsx — which would pull fflate into the extension's boot graph
 * and into registration.test.ts's jest module graph.
 */

type Phase = 'idle' | 'intaking' | 'uploading' | 'halted' | 'finished';

interface State {
  phase: Phase;
  rows: UploadRow[];
  /** -1 when nothing is in flight. Drives FileItem's `isCurrentFile` border. */
  activeIndex: number;
  haltMessage: string | null;
  /**
   * Whether the halt is one a force could answer, which is what decides between
   * the dialog's two exits and its one — see BatchOutcome's `rejected`/`failed`
   * split in utils/uploadBatch.ts.
   */
  canForce: boolean;
  /** True while Stop's DELETEs are in flight. Both dialog buttons guard on it. */
  isResolving: boolean;
}

type Action =
  | { type: 'intakeStarted' }
  | { type: 'intakeFailed' }
  | { type: 'filesReady'; rows: UploadRow[] }
  | { type: 'batchStarted' }
  | { type: 'fileStarted'; index: number }
  | { type: 'fileProgress'; index: number; progress: number }
  | { type: 'fileSettled'; index: number; status: UploadStatus; error?: string }
  | { type: 'halted'; message: string; canForce: boolean }
  | { type: 'batchFinished' }
  | { type: 'rollbackStarted' }
  | { type: 'reset' };

const INITIAL: State = {
  phase: 'idle',
  rows: [],
  activeIndex: -1,
  haltMessage: null,
  canForce: false,
  isResolving: false,
};

/**
 * Rows are replaced by index, never mutated: develop's Continue path assigned into
 * a row held in state (useDicomUpload.ts:174-175) and React bailed out of the
 * re-render, and its accumulated-UID setter did the same thing with a Set
 * (`setProcessedStudyInstanceUIDs(prev => prev.add(uid))`, :271, §6 defect 6).
 */
function replaceRow(rows: UploadRow[], index: number, patch: Partial<UploadRow>): UploadRow[] {
  const next = rows.slice();
  next[index] = { ...next[index], ...patch };
  return next;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'intakeStarted':
      return { ...INITIAL, phase: 'intaking' };

    case 'intakeFailed':
    case 'reset':
      return INITIAL;

    case 'filesReady':
      return { ...INITIAL, phase: 'uploading', rows: action.rows };

    case 'batchStarted':
      return {
        ...state,
        phase: 'uploading',
        haltMessage: null,
        canForce: false,
        isResolving: false,
      };

    case 'fileStarted':
      return {
        ...state,
        activeIndex: action.index,
        rows: replaceRow(state.rows, action.index, {
          status: UploadStatus.InProgress,
          progress: 0,
          error: undefined,
        }),
      };

    case 'fileProgress': {
      // Guarded on InProgress, as develop's reducer was (uploadReducer.ts:35): a
      // late progress event for a row that has already settled would drag a
      // finished bar backwards.
      const row = state.rows[action.index];

      // The unchanged-percentage bail is load-bearing at D9's scale, not tidiness:
      // runUploadBatch rounds to whole percent before dispatching, so most of the
      // XHR's events repeat a value, and every one that got through here allocated
      // a fresh N-element array in replaceRow and re-ran three O(N) passes in
      // `stats` — at 2,000 instances, for nothing.
      return row?.status === UploadStatus.InProgress && row.progress !== action.progress
        ? { ...state, rows: replaceRow(state.rows, action.index, { progress: action.progress }) }
        : state;
    }

    case 'fileSettled':
      return {
        ...state,
        activeIndex: -1,
        rows: replaceRow(state.rows, action.index, {
          status: action.status,
          error: action.error,
          // Guarded like `fileProgress` above, not just indexed: Task 7's `reset`
          // can empty `rows` while a request is still in flight, and a settle
          // landing after that would otherwise throw inside the reducer.
          progress:
            action.status === UploadStatus.Success ? 100 : (state.rows[action.index]?.progress ?? 0),
        }),
      };

    case 'halted':
      return {
        ...state,
        phase: 'halted',
        activeIndex: -1,
        haltMessage: action.message,
        canForce: action.canForce,
      };

    case 'batchFinished':
      return { ...state, phase: 'finished', activeIndex: -1 };

    case 'rollbackStarted':
      return { ...state, isResolving: true };

    default:
      return state;
  }
}

export interface UploadStats {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  totalProgress: number;
}

interface UseDicomUploadOptions {
  onComplete: () => void;
  onStarted: () => void;
}

export function useDicomUpload({ onComplete, onStarted }: UseDicomUploadOptions) {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [state, dispatch] = useReducer(reducer, INITIAL);

  // Plan D-9. Files live here, never in reducer state.
  const filesRef = useRef<File[]>([]);

  // Plan D-2. The overlay masks the drop target, but a mask is presentation — the
  // one-drop-one-study invariant (D3) is enforced here so it survives a restyle.
  const isIntakingRef = useRef(false);

  // D-10. develop called onStarted twice, from handleDrop:412 and processFiles:254.
  const hasStartedRef = useRef(false);

  /**
   * §5.4. A useRef, not state: develop's `setProcessedStudyInstanceUIDs(prev =>
   * prev.add(uid))` (:271) mutates and returns the SAME reference, so React bails
   * out of the re-render — inert there only because the value was read via
   * Array.from inside a callback rather than rendered. A ref says outright that
   * this is not render state.
   */
  const studyUidsRef = useRef<Set<string>>(new Set());
  const abortRef = useRef<AbortController | null>(null);

  // Guards runFrom itself, not its callers: Task 7's continueWithForce calls
  // runFrom directly from a button, outside handleFiles and its isIntakingRef.
  // Blocks two CONCURRENT runFrom entries -- a resumed batch and a
  // double-clicked Continue -- which would otherwise let two runs share
  // abortRef and studyUidsRef at once. It does NOT cover a halted run losing
  // its accumulated UIDs to a later, non-overlapping drop; that is sequential
  // state loss, not a race, and is guarded separately in handleFiles.
  const isRunningRef = useRef(false);

  // Files this drop has already put on the server, across a halt and a resume.
  // studyUidsRef cannot answer it: it counts STUDIES, and the cancel notice below
  // has to name files.
  const uploadedCountRef = useRef(0);

  const isUnmountedRef = useRef(false);

  /**
   * Whole-milestone review C1. D7 assumed the user cannot leave while an upload
   * runs; the modal's close X says otherwise, and nothing else here would notice.
   * useWorkListToolbarActions.tsx:43's `closeButton: false` is silently dropped —
   * Modal.tsx:16-24 destructures seven keys and that is not one of them — and
   * Modal.tsx:32 renders DialogContent without `unstyled`, so Dialog.tsx:116's
   * `{!unstyled && <DialogPrimitive.Close>}` renders the X unconditionally.
   * Clicking it calls hide(), which unmounts this component mid-batch, leaving
   * runUploadBatch's loop as a detached closure that uploads the rest of the study
   * invisibly and dispatches into a dead reducer: no progress, no cancel, and a
   * rejection that opens no dialog and rolls nothing back.
   *
   * TWO halves, because the X is reachable in two phases. Aborting stops a batch
   * already running; the ref stops one that has not started yet, which the abort
   * cannot do — during 'intaking' abortRef is still null, so an X clicked there
   * would let the intake finish and start the ENTIRE upload with no UI at all.
   * Both handleFiles and runFrom read the ref before they do anything else.
   *
   * Empty deps, so the cleanup runs at unmount and not on every render. It does
   * not undo the prefix already on the server; the `cancelled` branch below names
   * that in a toast, which still reaches the user because uiNotificationService
   * outlives this component.
   *
   * The setup line is not redundant with the ref's initial `false`: React 18
   * StrictMode mounts, unmounts and remounts on the SAME fiber, and a ref survives
   * that, so a cleanup-only assignment would leave the remounted instance
   * permanently "unmounted" and every runFrom would return without uploading —
   * silently, with no error. Not reachable today (nothing in platform/ enables
   * StrictMode) and one line to stay safe when it is, or when React 19 lands.
   */
  useEffect(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;
      abortRef.current?.abort();
    };
  }, []);

  const runFrom = useCallback(
    async (indices: number[], force: boolean) => {
      if (isUnmountedRef.current || isRunningRef.current) {
        return;
      }
      isRunningRef.current = true;

      try {
        const controller = new AbortController();
        abortRef.current = controller;

        dispatch({ type: 'batchStarted' });

        const outcome = await runUploadBatch(filesRef.current, indices, {
          force,
          signal: controller.signal,
          studyUids: studyUidsRef.current,
          callbacks: {
            onFileStarted: (index: number) => dispatch({ type: 'fileStarted', index }),
            onFileProgress: (index: number, progress: number) =>
              dispatch({ type: 'fileProgress', index, progress }),
            onFileSettled: (index: number, status: UploadStatus, error?: string) => {
              if (status === UploadStatus.Success) {
                uploadedCountRef.current += 1;
              }

              dispatch({ type: 'fileSettled', index, status, error });
            },
          },
        });

        switch (outcome.type) {
          case 'completed':
            dispatch({ type: 'batchFinished' });
            // §5.6 + D10. onComplete() fires as soon as the last POST resolves, and
            // the cross-backend gap is ACCEPTED KNOWINGLY: uploads go to the viewer
            // API while the study list QIDOs the PACS, so hide(); onRefresh(); can
            // land on a list that lacks the study. Recorded so it is not
            // rediscovered as a bug; the fix, if it bites, is to confirm each UID
            // through QIDO with M2's usePolling before closing.
            onComplete();
            return;

          case 'cancelled':
            dispatch({ type: 'batchFinished' });

            // Whole-milestone review I4, user ruling. Cancel keeps its semantics —
            // no rollback, and it closes the modal exactly as a completion does
            // (§7.1 check 9) — but it used to share `completed`'s body outright, so
            // onRefresh() then showed the half-uploaded study on the worklist with
            // nothing said. Naming the prefix is the whole change; the user can
            // delete the study from the list.
            if (uploadedCountRef.current > 0) {
              uiNotificationService?.show({
                title: 'Upload',
                message: `Upload cancelled. ${uploadedCountRef.current} file(s) were already uploaded and remain on the server.`,
                type: 'warning',
                duration: 5000,
              });
            }

            onComplete();
            return;

          case 'rejected':
            dispatch({ type: 'halted', message: outcome.message, canForce: true });
            return;

          case 'failed':
            // canForce: false. §5.4's Continue re-sends every REMAINING file with
            // x-skip-validation, which answers a backend that judged the content
            // and nothing else — offering it for a 502 or a dropped connection
            // invites the user to disable validation for the rest of the study to
            // fix a network fault.
            dispatch({ type: 'halted', message: outcome.message, canForce: false });
            return;

          case 'unauthorized':
            // D8. No dialog, and no onComplete: viewerHttp's onUnauthorized has
            // already run handleUnauthenticated(), which redirects to the login URL.
            dispatch({ type: 'batchFinished' });
            return;

          default: {
            // Throws rather than returns, and the catch below turns it into a halt
            // the user can leave: a fifth BatchOutcome falling through here used to
            // strand phase 'uploading' with a spinner that never stops and a Cancel
            // button whose controller the finally had already nulled.
            const unhandled: never = outcome;
            throw new Error(`Unhandled upload outcome: ${JSON.stringify(unhandled)}`);
          }
        }
      } catch (error) {
        // `catch (error)`, never a bare `catch {}`: @babel/plugin-transform-regenerator
        // reads `.name` off the null param under the `test` env and throws at
        // transform time. useOrganReview's `submit` carries the full finding.
        console.error(`[sycai] upload run failed ${describeError(error)}`);

        // Halted, not finished: 'finished' renders a progress list with no button
        // at all, so it would depend on the very close X D7 assumed was absent.
        // canForce is false for the same reason 'failed' sets it — nothing that
        // reaches here is a judgement the backend made about a file.
        dispatch({
          type: 'halted',
          message: error instanceof Error ? error.message : 'Upload failed',
          canForce: false,
        });
      } finally {
        // Cleared here, not left to the next runFrom to overwrite: abortRef
        // must mean "a batch is running right now", or a cancel issued after
        // this run has settled either aborts a dead controller (silently
        // doing nothing) or aborts one runFrom already replaced.
        abortRef.current = null;
        isRunningRef.current = false;
      }
    },
    [onComplete, uiNotificationService]
  );

  const handleFiles = useCallback(
    async (dropped: File[]) => {
      // Ahead of every other guard, because this one is about whether ANY of this
      // should run at all. react-dropzone@10 resolves a dropped DataTransfer
      // asynchronously (file-selector's fromEvent), so onFiles can arrive after an
      // X has already unmounted us: without this, onStarted() re-shows the modal on
      // a fresh, empty drop zone, and the intake's own toasts fire over a worklist
      // whose upload modal is gone. runFrom's copy of the check stops the upload
      // itself; this stops everything before it.
      if (isUnmountedRef.current) {
        return;
      }

      if (isIntakingRef.current || dropped.length === 0) {
        return;
      }

      // Sequential state loss, not a race: isIntakingRef/isRunningRef are both
      // already false by the time a halted run's next handleFiles call
      // arrives (each cleared in its own finally as soon as runFrom returned),
      // so nothing here overlaps -- the loop below would just proceed and
      // overwrite studyUidsRef with a fresh Set before ever reaching runFrom,
      // orphaning the previous run's UIDs with no way for Task 7's Stop to
      // roll them back. studyUids.size > 0 means exactly that: a run
      // accumulated UIDs and neither a rollback nor onComplete has cleared
      // them. A successful run can't trip this -- onComplete() calls hide(),
      // which unmounts this component and takes every ref with it.
      if (studyUidsRef.current.size > 0) {
        return;
      }

      isIntakingRef.current = true;
      dispatch({ type: 'intakeStarted' });

      if (!hasStartedRef.current) {
        hasStartedRef.current = true;
        // Safe despite re-showing the modal: useWorkListToolbarActions.tsx:41-43
        // spreads the SAME uploadProps object its closure was born with, so
        // options.content keeps its function identity and ModalProvider does not
        // remount us — a remount here would discard the whole upload (plan D-10).
        onStarted();
      }

      try {
        // Plan D-3. fflate inflates any entry under 320 000 compressed bytes with
        // inflateSync ON THE CALLING THREAD, and a DICOM study is thousands of
        // such entries — so extraction blocks. Yielding a frame first is what lets
        // React paint the overlay; without it the user sees nothing until the
        // whole archive is done.
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

        const { files, unreadable } = await expandDropped(dropped);

        if (unreadable > 0) {
          // §5.1 + whole-milestone review I3. dicomIntake counts these and returns
          // the count; deciding they are worth saying is this layer's job. Under D3
          // a silent skip means instances go missing from a study and the rest
          // uploads looking complete.
          uiNotificationService?.show({
            title: 'Upload',
            message: `Skipped ${unreadable} file(s) using an unsupported compression method.`,
            type: 'warning',
            duration: 5000,
          });
        }

        if (files.length === 0) {
          dispatch({ type: 'intakeFailed' });

          // User ruling on Task 3's own review: every other failure path here shows a
          // toast, and a junk-only or all-unreadable drop should not silently reset.
          // No `develop` original for this string -- it is new.
          uiNotificationService?.show({
            title: 'Upload',
            message: 'No DICOM files found in the drop.',
            type: 'error',
            duration: 5000,
          });
          return;
        }

        filesRef.current = files;
        studyUidsRef.current = new Set();
        uploadedCountRef.current = 0;
        // Belt-and-suspenders alongside runFrom's own finally: a fresh drop
        // starts a fresh run, so no controller from a prior run should remain
        // reachable for a cancel to find.
        abortRef.current = null;

        dispatch({
          type: 'filesReady',
          rows: files.map(file => ({
            name: file.name,
            size: file.size,
            status: UploadStatus.NotStarted,
            progress: 0,
          })),
        });

        // Started HERE, not from an effect. develop drove the loop from a
        // useEffect keyed on the file list (useDicomUpload.ts:303-317), which
        // re-ran on every row change and needed four guard conditions to avoid
        // re-entering itself. A drop is an event; the upload is its consequence.
        await runFrom(
          files.map((unused: File, index: number) => index),
          false
        );
      } catch (error) {
        dispatch({ type: 'intakeFailed' });

        // §5.1: dicomIntake throws; the HOOK decides what the user sees. develop
        // handed the notification service to the size check itself
        // (fileSizeCheck.ts:7-26).
        uiNotificationService?.show({
          title: 'Upload',
          message:
            error instanceof IntakeError ? error.message : 'Could not read the dropped files',
          type: 'error',
          duration: 5000,
        });

        if (!(error instanceof IntakeError)) {
          console.error(`[sycai] intake failed ${describeError(error)}`);
        }
      } finally {
        isIntakingRef.current = false;
      }
    },
    [onStarted, runFrom, uiNotificationService]
  );

  /**
   * §5.4. Resumes from the first non-Success file with force: true. "Every
   * remaining file", not just the rejected one, is correct only under D3 — the
   * user is accepting the mismatch FOR THIS STUDY. develop did the same
   * (useDicomUpload.ts:172) but recorded nothing, which is why it reads as
   * over-broad there.
   *
   * develop also reset the first remaining file's uploader in place before
   * resuming (:174-175). That has no analogue here: ensurePreamble is applied per
   * file inside the loop, so a resumed file is prepared exactly as a first attempt
   * is.
   */
  const continueWithForce = useCallback(async () => {
    const indices = state.rows
      .map((unused: UploadRow, index: number) => index)
      .filter(index => state.rows[index].status !== UploadStatus.Success);

    await runFrom(indices, true);
  }, [state.rows, runFrom]);

  // isResolving is reducer state, not a synchronous flag: two invocations of
  // stopAndRollback within one task both close over the SAME render's `false`,
  // because React does not flush between them — the gap continueWithForce doesn't
  // have, since runFrom sets isRunningRef.current synchronously. Set before the
  // dispatch it guards, so a second call in the same task observes it.
  const isStoppingRef = useRef(false);

  /**
   * §5.4's Stop: delete every accumulated UID, then return to the drop state.
   * Under D3 that is "abandon this study", not "delete everything the user has
   * ever uploaded".
   */
  const stopAndRollback = useCallback(async () => {
    if (isStoppingRef.current) {
      return;
    }
    isStoppingRef.current = true;

    try {
      dispatch({ type: 'rollbackStarted' });

      const failed = await rollbackStudies(studyUidsRef.current);

      studyUidsRef.current = new Set();
      filesRef.current = [];
      uploadedCountRef.current = 0;
      // Cleared so a second drop in the same modal raises onStarted again, matching
      // the first drop's behaviour rather than silently skipping it.
      hasStartedRef.current = false;

      dispatch({ type: 'reset' });

      if (failed.length > 0) {
        // §6 defect 14. develop swallowed these to console.error (:243) — a failed
        // rollback leaves orphaned data in the PACS and the user needs to know.
        uiNotificationService?.show({
          title: 'Upload',
          message: `Could not remove ${failed.length} uploaded stud${failed.length === 1 ? 'y' : 'ies'} from the server.`,
          type: 'error',
          duration: 5000,
        });
      }
    } finally {
      isStoppingRef.current = false;
    }
  }, [uiNotificationService]);

  /**
   * §5.4. Routes through onComplete(), because UPSTREAM PASSES NO onCancel:
   * useWorkListToolbarActions.tsx:34-44 passes dataSource, onComplete and onStarted
   * only. develop had a third prop, but only because its own WorkList patch
   * supplied it — D1 removes that patch, so the prop goes with it.
   *
   * Aborting is all this does. runUploadBatch settles the in-flight row Cancelled
   * and returns { type: 'cancelled' }, and runFrom's own 'cancelled' branch closes
   * the modal through onComplete() exactly as a completion does (§7.1 check 9),
   * differing from it only by the toast that names the uploaded prefix (I4).
   */
  const cancelAll = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const stats = useMemo<UploadStats>(() => {
    const { rows } = state;
    const completedFiles = rows.filter(row => row.status === UploadStatus.Success).length;
    const failedFiles = rows.filter(row => row.status === UploadStatus.Failed).length;

    return {
      totalFiles: rows.length,
      completedFiles,
      failedFiles,
      totalProgress: rows.length
        ? rows.reduce((sum, row) => sum + row.progress, 0) / rows.length
        : 0,
    };
  }, [state]);

  return {
    phase: state.phase,
    rows: state.rows,
    activeIndex: state.activeIndex,
    haltMessage: state.haltMessage,
    canForce: state.canForce,
    isResolving: state.isResolving,
    stats,
    handleFiles,
    continueWithForce,
    stopAndRollback,
    cancelAll,
  };
}
